import classes from './Schedule.module.css';
import { Appointments, UpcomingList } from './Elements';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  fetchSalesOrders,
  SalesOrderInfo,
  selectSalesOrders,
} from '../../store/slices/salesOrders';
import React, { useMemo } from 'react';
import Calendar from 'react-calendar';
import moment from 'moment';
import { WeeklyAppointments } from './Elements/WeeklyAppointments';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IDoctor, fetchDoctors, selectDoctors } from '../../store/slices/doctors';
import {
  getTimeSlots,
  IAccountInfo,
  IPerson,
  selectAccountInfo,
  selectLocationId,
  selectPersonLoggedIn,
  selectUserPublicDesignationId,
  selectUserRole,
} from '../../store/slices/auth';
import { authorizeClient } from '../../networking/client';
import { toast } from 'react-toastify';
import POSITION = toast.POSITION;
import { CheckIn } from '../screens';
import extendCollapseIcon2 from '../../assets/icons/back-arrow2.svg';
import extendCollapseIcon from '../../assets/icons/back-arrow.svg';
import PatientInformations from '../../components/PatientInformation/PatientInformation';
import { IS_CONSULTATION, IS_TEST } from '../../enums/enums';
import { fetchProductCategories } from '../../store/slices/productCategories';
import { useTranslation } from '../../hooks/useTranslations';

const SCHEDULE_TAB = 'schedule';
const CHECKIN_TAB = 'check-in';
const UPCOMPINGS_TAB = 'upcomings';

const Schedule: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translations } = useTranslation();

  const salesOrders: SalesOrderInfo[] = useAppSelector(selectSalesOrders);
  const [weekMode, setWeekMode] = React.useState<boolean>(false);
  const [selectedDate, setSelectedDate] = React.useState<Date>(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  });
  const [selectedRange, setSelectedRange] = React.useState<string[]>([]);
  const [searchParams] = useSearchParams();
  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const [selectedTab, setSelectedTab] = React.useState<string>(() => {
    const tabFromUrl = searchParams.get('tab');
    return (
      tabFromUrl ??
      (accountInfo.serves_diagnostics
        ? CHECKIN_TAB
        : accountInfo.serves_consultations
        ? SCHEDULE_TAB
        : '')
    );
  });
  const [expanded, setExpanded] = React.useState<boolean>(true);
  const doctors: IDoctor[] = useAppSelector<IDoctor[]>(selectDoctors);
  const designationId = useAppSelector<number | undefined>(selectUserPublicDesignationId);
  const personLoggedIn = useAppSelector<IPerson | undefined>(selectPersonLoggedIn);
  const locationId = useAppSelector<IAccountInfo>(selectLocationId);
  const userRole: string = useAppSelector<string>(selectUserRole);
  const [loadingUpdate, setLoadingUpdate] = React.useState<boolean>(false);

  let calendarType = searchParams.get('type');
  const lang = localStorage.getItem('lang') || 'en';


  const handleAppointmentUpdate = async (
    salesOrder: SalesOrderInfo,
    start: Date,
    end: Date,
    callback: () => void
  ) => {
    setLoadingUpdate(true);
    const body = {
      ta_id: accountInfo.account_id,
      ref_ta_id: salesOrder.sales_order.ta_id,
      product_order_id: salesOrder.sales_order.sales_order_external_id,
      product_order_item_id: salesOrder.sales_order_item?.sales_order_item_id,
      appointment: {
        start: start,
        end: end,
      },
    };
    try {
      await authorizeClient.put('/core/merchant/order_product/appointment_reschedule', body);
      await dispatch(fetchSalesOrders());
      setLoadingUpdate(false);
      callback();
      toast.success(translations('appointment.update.success'), {
        position: POSITION.BOTTOM_RIGHT,
      });
    } catch (_) {
      toast.error(translations('placeholder.something.went.wrong'), {
        position: POSITION.BOTTOM_RIGHT,
      });
    }
  };

  const TABS = useMemo(() => {
    if (accountInfo.serves_consultations && accountInfo.serves_diagnostics) {
      return [CHECKIN_TAB, SCHEDULE_TAB, UPCOMPINGS_TAB];
    }
    if (accountInfo.serves_diagnostics) {
      return [CHECKIN_TAB, SCHEDULE_TAB, UPCOMPINGS_TAB];
    }
    if (accountInfo.serves_consultations) {
      return [SCHEDULE_TAB, UPCOMPINGS_TAB];
    }
    return [];
  }, [accountInfo]);

  React.useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    setSelectedTab(
      tabFromUrl ??
        (accountInfo.serves_diagnostics
          ? CHECKIN_TAB
          : accountInfo.serves_consultations
          ? SCHEDULE_TAB
          : '')
    );
  }, [searchParams, accountInfo.serves_consultations, accountInfo.serves_diagnostics]);

  React.useEffect(() => {
    if (selectedTab === SCHEDULE_TAB) {
      navigate('/web/schedule?tab=schedule', { replace: true });
    }
  }, [selectedTab, navigate]);

  React.useEffect(() => {
    dispatch(fetchSalesOrders());
    dispatch(getTimeSlots());
    dispatch(
      fetchProductCategories({
        productCategoryId: Number(process.env.REACT_APP_CATEGORY_ID),
        merchantAccountNumber: accountInfo.account_number,
      })
    );
  }, [dispatch]);

  React.useEffect(() => {
    if (locationId) {
      dispatch(fetchDoctors());
    }
  }, [dispatch, locationId]);

  const salesOrdersToDisplay: SalesOrderInfo[] = React.useMemo(() => {
    const doctorLoggedIn = doctors.find(
      (doctor) => parseInt(doctor.person_id) == Number(personLoggedIn?.person_id)
    );
    return salesOrders
      .filter((salesOrder) => {
        // if (
        //   !salesOrder.sales_order_appointment?.start ||
        //   !salesOrder.sales_order_appointment.end_time
        // ) {
        //   return false;
        // }

        // filter out the consultations from the non-mobile clinic locations
        if (
          !accountInfo.serves_consultations &&
          IS_CONSULTATION.includes(salesOrder.service_service_group.service_group_id)
        )
          return false;
        // if location doesn't serve diagnostics then filter them out
        if (
          !accountInfo.serves_diagnostics &&
          IS_TEST.includes(salesOrder.service_service_group.service_group_id)
        )
          return false;

        if (
          salesOrder.work_order?.work_order_status_id == 2 ||
          salesOrder.work_order?.work_order_status_id == 3 ||
          salesOrder.work_order?.work_order_status_id == 4 ||
          salesOrder.work_order_service_diagnostic
        )
          return false;
        // if (userRole === 'consultation executioner' || userRole === 'care center agent') {
        //   // Diagnostics calendar
        //   if (
        //     !(
        //       salesOrder.service_service_group.service_group_id === 1 ||
        //       salesOrder.service_service_group.service_group_id === 4
        //     )
        //   )
        //     return false;
        // }
        // if (calendarType === 'C') {
        //   // Consultations calendar
        //   if (
        //     !(
        //       salesOrder.service_service_group.service_group_id === 3 ||
        //       salesOrder.service_service_group.service_group_id === 7 ||
        //       salesOrder.service_service_group.service_group_id === 8
        //     )
        //   )
        //     return false;
        // }
        if ((Number(designationId) === 3 || Number(designationId) === 4) && doctorLoggedIn) {
          // don't show diagnostics for doctors and nutritionsits
          if (
            salesOrder?.service_service_group.service_group_id === 1 ||
            salesOrder?.service_service_group.service_group_id === 4
          )
            return false;
          if (
            !doctorLoggedIn.person_appointments.find(
              (appointment) =>
                appointment.sales_order_id == salesOrder?.sales_order.sales_order_id &&
                appointment.sales_order_item_id == salesOrder?.sales_order_item.sales_order_item_id
            )
          )
            return false;
        }
        if (weekMode) {
          for (let dateString of selectedRange) {
            if (
              moment(salesOrder.sales_order_appointment?.start).isSame(moment(dateString), 'day') ||
              !salesOrder.sales_order_appointment.start
            ) {
              return true;
            }
          }
          return false;
        }
        return (
          moment(salesOrder.sales_order_appointment?.start).isSame(selectedDate, 'day') ||
          !salesOrder.sales_order_appointment?.start
        );
      })
      .sort((a, b) => {
        if (a.sales_order_appointment?.start && b.sales_order_appointment?.start)
          return a.sales_order_appointment.start < b.sales_order_appointment.start ? -1 : 1;
        return 1;
      });
  }, [
    salesOrders,
    selectedDate,
    selectedRange,
    weekMode,
    calendarType,
    designationId,
    doctors,
    personLoggedIn,
    userRole,
  ]);

  React.useEffect(() => {
    if (weekMode) {
      const currentDate = moment(selectedDate);
      const weekStart = currentDate.clone().startOf('week');
      let days: string[] = [];
      for (let i = 1; i <= 7; i++) {
        let ds = moment(weekStart);
        ds = moment(ds.add(i, 'days').toDate());
        days.push(ds.format());
      }
      setSelectedRange(days);
    }
  }, [weekMode]);

  return (
    <div className={classes.root}>
      <div className={classes.tabsContainer}>
        {TABS.map((tab) => (
          <div
            key={tab}
            className={`${classes.tab} ${selectedTab === tab ? classes.selectedTab : ''}`}
            onClick={() => {
              setSelectedTab(tab);
              navigate(`/web/schedule?tab=${tab}`, { replace: true });
            }}
          >
            {translations(tab)}
          </div>
        ))}
      </div>
      {selectedTab === SCHEDULE_TAB && (
        <div className={classes.container}>
          <div className={classes.collapsibleContainer}>
            {expanded ? (
              <div className={classes.calendarAndUpcomingContainer}>
                <img
                  src={extendCollapseIcon}
                  alt="extend"
                  onClick={() => setExpanded(false)}
                  className={classes.collapseIcon}
                />
                <div className={classes.calendar}>
                  <Calendar
                    locale={lang}
                    tileContent={(props) => {
                      if (props.view === 'month') {
                        const filtered = salesOrders.filter((salesOrder) => {
                          if (
                            salesOrder.work_order?.work_order_status_id == 2 ||
                            salesOrder.work_order?.work_order_status_id == 3 ||
                            salesOrder.work_order?.work_order_status_id == 4 ||
                            salesOrder.work_order_service_diagnostic
                          )
                            return false;
                          // filter out the consultations from the non-mobile clinic locations
                          if (
                            !accountInfo.serves_consultations &&
                            (salesOrder.service_service_group.service_group_id === 3 ||
                              salesOrder.service_service_group.service_group_id === 7 ||
                              salesOrder.service_service_group.service_group_id === 8 ||
                              salesOrder.service_service_group.service_group_id === 11)
                          )
                            return false;
                          const doctorLoggedIn = doctors.find(
                            (doctor) =>
                              parseInt(doctor.person_id) == Number(personLoggedIn?.person_id)
                          );
                          if (
                            (Number(designationId) === 3 || Number(designationId) === 4) &&
                            doctorLoggedIn
                          ) {
                            // don't show diagnostics for doctors and nutritionsits
                            if (
                              salesOrder?.service_service_group.service_group_id === 1 ||
                              salesOrder?.service_service_group.service_group_id === 4
                            )
                              return false;
                            if (
                              !doctorLoggedIn.person_appointments.find(
                                (appointment) =>
                                  appointment.sales_order_id ==
                                    salesOrder?.sales_order.sales_order_id &&
                                  appointment.sales_order_item_id ==
                                    salesOrder?.sales_order_item.sales_order_item_id
                              )
                            )
                              return false;
                          }
                          if (!salesOrder.sales_order_appointment?.start) return false;
                          return moment(salesOrder.sales_order_appointment?.start).isSame(
                            props.date,
                            'day'
                          );
                        });
                        const momentDate = moment(props.date);
                        return (
                          <div
                            className={
                              props.date.getMonth() !== props.activeStartDate.getMonth()
                                ? classes.neighboringMonth
                                : (
                                    weekMode
                                      ? selectedRange.includes(momentDate.format())
                                      : momentDate.isSame(selectedDate)
                                  )
                                ? classes.selectedTile
                                : filtered.length > 0
                                ? classes.customCalendarTileBold
                                : classes.customCalendarTile
                            }
                          >
                            {props.date.getMonth() === props.activeStartDate.getMonth() &&
                              !moment(props.date).isSame(selectedDate) &&
                              filtered.length > 0 && <div className={classes.blueDot} />}
                            {props.date.getDate()}
                          </div>
                        );
                      }
                      return <></>;
                    }}
                    value={selectedDate}
                    onClickDay={(date) => {
                      if (weekMode) {
                        const currentDate = moment(date);
                        const weekStart = currentDate.clone().startOf('week');
                        let days: string[] = [];
                        for (let i = 1; i <= 7; i++) {
                          let ds = moment(weekStart);
                          ds = moment(ds.add(i, 'days').toDate());
                          days.push(ds.format());
                        }
                        setSelectedRange(days);
                      } else {
                        date.setHours(0, 0, 0, 0);
                        setSelectedRange([]);
                        setSelectedDate(date);
                      }
                    }}
                    formatShortWeekday={(locale, date) => moment(date).format('dd').slice(0, 1)}
                    className="custom-calendar"
                  />
                </div>
                <div className={classes.upcomingList}>
                  <UpcomingList salesOrders={salesOrdersToDisplay} />
                </div>
              </div>
            ) : (
              <div className={classes.collapsedContainer}>
                <img
                  src={extendCollapseIcon2}
                  alt="extend"
                  onClick={() => setExpanded(true)}
                  className={classes.extendIcon}
                />
              </div>
            )}
          </div>
          <div className={classes.appointmentsContainer}>
            {weekMode ? (
              <WeeklyAppointments
                selectedSalesOrders={salesOrdersToDisplay}
                selectedRange={selectedRange}
                toggleDailyView={() => setWeekMode(false)}
                loadingUpdate={loadingUpdate}
                handleUpdateAppointment={handleAppointmentUpdate}
              />
            ) : (
              <Appointments
                salesOrders={salesOrdersToDisplay}
                selectedDate={selectedDate}
                toggleWeeklyView={() => setWeekMode(true)}
                loadingUpdate={loadingUpdate}
                handleUpdateAppointment={handleAppointmentUpdate}
              />
            )}
          </div>
        </div>
      )}
      {selectedTab === 'check-in' && <CheckIn />}
      {selectedTab === 'upcomings' && (
        // <div className={classes.upcomingList}>
        //   <UpcomingList salesOrders={salesOrdersToDisplay} />
        // </div>
        <PatientInformations />
      )}
    </div>
  );
};

export { Schedule };
